import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Search, PackageSearch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderLookup() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const validate = () => {
    const e = {};
    if (!orderId.trim()) e.orderId = "Required";
    if (!email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setOrder(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/orders/${orderId.trim()}`);
      const data = res.data;
      // Email gating — order id alone is not enough.
      if (
        (data.customer_email || "").trim().toLowerCase() !==
        email.trim().toLowerCase()
      ) {
        setNotFound(true);
      } else {
        setOrder(data);
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOrder(null);
    setNotFound(false);
    setOrderId("");
    setEmail("");
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1F2924] flex flex-col">
      <Header />
      <main
        data-testid="order-lookup-page"
        className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-10 py-16 lg:py-24"
      >
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#4A5D53] hover:text-[#D96C2B] transition-colors mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to shop
        </Link>

        <div className="text-xs uppercase tracking-[0.3em] text-[#D96C2B] mb-5">
          Order Status
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-[#2C4C3B]">
          Look up <span className="italic">your order.</span>
        </h1>
        <p className="mt-4 text-[#4A5D53] max-w-lg leading-relaxed">
          Paste the order ID from your confirmation along with the email used at
          checkout — we&apos;ll surface the latest status.
        </p>

        {!order && (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="mt-12 grid gap-5 max-w-xl"
            data-testid="order-lookup-form"
          >
            <div>
              <Label
                htmlFor="orderId"
                className="text-xs uppercase tracking-widest text-[#4A5D53]"
              >
                Order ID
              </Label>
              <Input
                id="orderId"
                data-testid="lookup-input-order-id"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="e.g. 8a3c1f2b-9d4e-..."
                className="mt-2 bg-white border-[#E5DFD3] focus-visible:ring-[#D96C2B] rounded-none font-mono text-sm"
              />
              {errors.orderId && (
                <div className="text-xs text-red-700 mt-1">{errors.orderId}</div>
              )}
            </div>

            <div>
              <Label
                htmlFor="lookup-email"
                className="text-xs uppercase tracking-widest text-[#4A5D53]"
              >
                Email used at checkout
              </Label>
              <Input
                id="lookup-email"
                type="email"
                data-testid="lookup-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="mt-2 bg-white border-[#E5DFD3] focus-visible:ring-[#D96C2B] rounded-none"
              />
              {errors.email && (
                <div className="text-xs text-red-700 mt-1">{errors.email}</div>
              )}
            </div>

            {notFound && (
              <div
                data-testid="lookup-not-found"
                className="px-4 py-3 bg-red-50 border border-red-200 text-red-800 text-sm"
              >
                We couldn&apos;t match an order with that ID and email. Double-check
                both and try again, or call 352-552-4074 for help.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="lookup-submit-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D96C2B] text-[#FDFBF7] uppercase tracking-wider text-sm hover:bg-[#B75718] transition-colors disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {loading ? "Searching…" : "Look up order"}
            </button>
          </form>
        )}

        {order && (
          <div
            data-testid="lookup-result"
            className="mt-12 bg-[#F4F1EA] p-8 lg:p-10"
          >
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div className="inline-flex items-center gap-3">
                <PackageSearch className="h-5 w-5 text-[#2C4C3B]" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4A5D53]">
                    Order
                  </div>
                  <div
                    className="font-mono text-sm text-[#1F2924]"
                    data-testid="lookup-order-id"
                  >
                    {order.order_id}
                  </div>
                </div>
              </div>
              <span
                data-testid="lookup-status"
                className="inline-flex items-center px-3 py-1.5 bg-[#2C4C3B] text-[#FDFBF7] text-[10px] uppercase tracking-widest"
              >
                {order.status}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5D53] mb-1">
                  Name
                </div>
                <div className="text-[#1F2924]">{order.customer_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5D53] mb-1">
                  Email
                </div>
                <div className="text-[#1F2924] break-all">
                  {order.customer_email}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5D53] mb-1">
                  Fulfillment
                </div>
                <div className="text-[#1F2924] capitalize">
                  {order.delivery_method === "pickup"
                    ? "Local Pickup"
                    : "Local Delivery"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5D53] mb-1">
                  Placed
                </div>
                <div className="text-[#1F2924]">
                  {new Date(order.created_at).toLocaleString()}
                </div>
              </div>
            </div>

            <ul className="space-y-2 text-sm border-t border-[#E5DFD3] pt-5">
              {order.items?.map((it) => (
                <li
                  key={it.product_id}
                  className="flex justify-between"
                  data-testid={`lookup-item-${it.product_id}`}
                >
                  <span>
                    {it.name} × {it.quantity}
                  </span>
                  <span>${(it.price * it.quantity).toFixed(2)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 pt-5 border-t border-[#E5DFD3] space-y-1.5 text-sm">
              <div className="flex justify-between text-[#4A5D53]">
                <span>Subtotal</span>
                <span>${order.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#4A5D53]">
                <span>Tax (7%)</span>
                <span>${order.tax_amount?.toFixed(2)}</span>
              </div>
              <div
                className="flex justify-between font-display text-xl text-[#2C4C3B] pt-1"
                data-testid="lookup-total"
              >
                <span>Total</span>
                <span>${order.total_amount?.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={reset}
              data-testid="lookup-reset-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#2C4C3B] text-[#2C4C3B] uppercase tracking-wider text-sm hover:bg-[#2C4C3B] hover:text-[#FDFBF7] transition-colors"
            >
              Look up another order
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
